import { FC } from "react";
import { IOrderCycleTimeStats } from "../../../models/statistcs/respons";
import OrderStatusStatsCard from "../order-status-stats-card";

interface IProps {
  stats?: IOrderCycleTimeStats;
  onChange: (stats: IOrderCycleTimeStats) => void;
  hideDateControls?: boolean;
}

const cycleColumns = [
  { labelKey: "orders", field: "ordersCount", width: 110 },
  {
    labelKey: "average",
    field: "averageCycleMinutes",
    format: "minutes" as const,
    width: 140,
  },
  {
    labelKey: "median",
    field: "medianCycleMinutes",
    format: "minutes" as const,
    width: 140,
  },
  {
    labelKey: "p90",
    field: "p90CycleMinutes",
    format: "minutes" as const,
    width: 140,
  },
  {
    labelKey: "p95",
    field: "p95CycleMinutes",
    format: "minutes" as const,
    width: 140,
  },
  { labelKey: "outliers", field: "outlierCount", width: 120 },
  {
    labelKey: "stage_coverage_rate",
    field: "stageCoverageRate",
    format: "percent" as const,
    width: 160,
  },
];

const OrderCycleTimeStats: FC<IProps> = ({
  stats,
  onChange,
  hideDateControls,
}) => (
  <OrderStatusStatsCard
    stats={stats}
    onChange={onChange}
    hideDateControls={hideDateControls}
    titleKey="order_cycle_time_stats"
    averageLabelKey="p90_order_cycle_time"
    averageField="p90CycleMinutes"
    includedField="includedOrderCount"
    includedLabelKey="included_orders"
    excludedField="excludedOrderCount"
    excludedTooltipKey="excluded_order_cycle_time_orders_tooltip"
    metrics={[
      {
        labelKey: "average_order_cycle_time",
        field: "averageCycleMinutes",
        format: "minutes",
      },
      { labelKey: "median", field: "medianCycleMinutes", format: "minutes" },
      { labelKey: "p95", field: "p95CycleMinutes", format: "minutes" },
      { labelKey: "max_label", field: "maxCycleMinutes", format: "minutes" },
      {
        labelKey: "candidate_completed_orders",
        field: "candidateCompletedOrderCount",
      },
      { labelKey: "outliers", field: "outlierCount" },
      {
        labelKey: "outlier_rate",
        field: "outlierRate",
        format: "percent",
      },
      {
        labelKey: "stage_coverage_rate",
        field: "stageCoverageRate",
        format: "percent",
        tooltipKey: "stage_coverage_rate_tooltip",
      },
      {
        labelKey: "cycle_start_fallback_orders",
        field: "datetimeStartOrderMissingCount",
      },
      {
        labelKey: "repeated_completed_status_orders",
        field: "repeatedCompletedStatusCount",
      },
    ]}
    tables={[
      {
        key: "areas",
        labelKey: "areas",
        rows: (value) => value.areas || [],
        rowKey: (row, index) => `area-${row.areaId ?? index}`,
        columns: [{ labelKey: "area", field: "areaName", width: 220 }, ...cycleColumns],
      },
      {
        key: "bucketDistribution",
        labelKey: "bucket_distribution",
        rows: (value) => value.bucketDistribution || [],
        rowKey: (row, index) => `bucket-${row.bucketKey ?? index}`,
        columns: [
          { labelKey: "cycle_duration_bucket", field: "bucketLabel", width: 220 },
          { labelKey: "orders", field: "ordersCount", width: 110 },
          {
            labelKey: "orders_rate",
            field: "ordersRate",
            format: "percent",
            width: 130,
          },
          {
            labelKey: "average",
            field: "averageCycleMinutes",
            format: "minutes",
            width: 140,
          },
        ],
      },
      {
        key: "phaseBreakdown",
        labelKey: "phase_breakdown",
        tooltipKey: "phase_breakdown_unattributed_tooltip",
        rows: (value) => value.phaseBreakdown || [],
        rowKey: (row, index) => `phase-${row.phaseKey ?? index}`,
        columns: [
          {
            labelKey: "phase",
            field: "phaseName",
            width: 240,
          },
          { labelKey: "orders", field: "ordersCount", width: 110 },
          {
            labelKey: "average",
            field: "averageMinutes",
            format: "minutes",
            width: 140,
          },
          {
            labelKey: "median",
            field: "medianMinutes",
            format: "minutes",
            width: 140,
          },
          {
            labelKey: "p90",
            field: "p90Minutes",
            format: "minutes",
            width: 140,
          },
          {
            labelKey: "total_minutes",
            field: "totalMinutes",
            format: "minutes",
            width: 150,
          },
          {
            labelKey: "share_of_known_cycle_minutes",
            field: "shareOfKnownCycleMinutes",
            format: "percent",
            width: 190,
          },
        ],
      },
      {
        key: "dataQuality",
        labelKey: "data_quality",
        rows: (value) => value.dataQuality || [],
        rowKey: (row, index) => `quality-${row.reasonKey ?? index}`,
        columns: [
          { labelKey: "reason", field: "reason", width: 360 },
          { labelKey: "orders", field: "ordersCount", width: 120 },
        ],
      },
    ]}
  />
);

export default OrderCycleTimeStats;
